import React, { useMemo, useEffect } from "react"
import i18n from "i18n"
import { itemTypesWithIcons, UnityEnum } from "constants/unity"
import DynamicElementDeleteAction from "components/DynamicElements/DynamicElementDeleteAction"
import { Typography, MenuItem, Select } from "@mui/material"
import DynamicElementEditAction from "components/DynamicElements/DynamicElementEditAction"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"

const renderTypeCell = (params, itemTypesWithIcons) => {
  const selectedOption = itemTypesWithIcons.find(
    option => option.id === params.value,
  )
  return (
    <>
      {selectedOption ? selectedOption.icon : <Typography>Unknown</Typography>}
    </>
  )
}

const renderEditCell = (params, options) => (
  <Select
    value={params.value}
    onChange={event =>
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value: event.target.value,
      })
    }
    fullWidth
  >
    {options.map(option => (
      <MenuItem key={option.id || option} value={option.id || option}>
        {option.icon || option}
        {option.type || ""}
      </MenuItem>
    ))}
  </Select>
)

export const useColumns = (
  apiRef,
  columnWidths,
  autoSave,
  isSaving,
  isSuccess,
  shouldShowButton,
) => {
  const { budgetMode } = useBudget2023Context()
  const isTender = budgetMode === "tender"

  return useMemo(
    () => [
      { field: "itemId", headerName: "ID" },
      {
        field: "code",
        headerName: i18n.t("dynamicElement.columns.code"),
        width: 100,
        editable: !apiRef && !isTender,
      },
      {
        field: "type",
        headerName: i18n.t("dynamicElement.columns.type"),
        width: columnWidths.type,
        editable: !apiRef && !isTender,
        renderCell: params => renderTypeCell(params, itemTypesWithIcons),
        renderEditCell: params => renderEditCell(params, itemTypesWithIcons),
      },
      {
        field: "description",
        headerName: i18n.t("dynamicElement.columns.description"),
        width: 320,
        editable: !apiRef,
        filterable: true,
      },
      {
        field: "unity",
        headerName: i18n.t("dynamicElement.columns.unity"),
        hide: true,
        type: "singleSelect",
        valueOptions: Object.values(UnityEnum),
        width: 90,
        editable: !apiRef && !isTender,
      },
      {
        field: "price",
        headerName: i18n.t("dynamicElement.columns.price"),
        width: 100,
        type: "string",
        editable: !apiRef && !isTender,
      },
      {
        field: "priceReduced",
        headerName:
          i18n.t("dynamicElement.columns.priceReduced") || "Precio reducido",
        width: 120,
        type: "string",
        editable: !apiRef,
      },
      ...(isTender
        ? []
        : [
            {
              field: "actions",
              headerName: "",
              type: "actions",
              width: 200,
              getActions: params => [
                <DynamicElementEditAction
                  key={`save-edit-${params.id}`}
                  isSaving={isSaving(params.row.itemId)}
                  isSuccess={isSuccess(params.row.itemId)}
                  shouldShow={shouldShowButton(params.row.itemId)}
                />,
                <DynamicElementDeleteAction
                  key={`delete-${params.id}`}
                  label="Eliminar"
                  params={params}
                  showInMenu={false}
                />,
              ],
            },
          ]),
    ],
    [columnWidths.type, isSaving, isSuccess, shouldShowButton],
  )
}
